import { useEffect, useMemo, useState } from 'react';
import apiService from '../../services/api';
import {
  NR2J_CONVERSION_MISSING_LABELS,
  NR2J_QUOTE_SEND_GATE_LABELS,
  computeContractSignatoryGateMissing,
  contactDisplayName,
  mapConversionMissingToOrgFields,
} from '../../utils/nr2jConstants';
import { contractSendGateLabel } from '../../utils/nr2jContractGateUi';
import { NR2J_CONTINUE_INTENTS } from '../../utils/nr2jContinuePreflight';
import { normalizeSiren, validateSirenInput } from '../../utils/nr2jOrgIdentifiers';

function orgFieldNeeded(missingKeys, field) {
  const mapped = mapConversionMissingToOrgFields(missingKeys);
  return mapped.includes(field);
}

export default function Nr2jContinueModal({
  open,
  requirements,
  templateOptions = [],
  onClose,
  onSaved,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [orgForm, setOrgForm] = useState({});
  const [signatoryMode, setSignatoryMode] = useState('select');
  const [signatoryContactId, setSignatoryContactId] = useState('');
  const [newContact, setNewContact] = useState({ prenom: '', nom: '', email: '', fonction: '' });
  const [primaryContact, setPrimaryContact] = useState({ prenom: '', nom: '', email: '' });
  const [validUntil, setValidUntil] = useState('');
  const [quoteContactId, setQuoteContactId] = useState('');
  const [templateVersionId, setTemplateVersionId] = useState('');

  const blocks = requirements?.blocks || [];
  const missingKeys = requirements?.missingKeys || [];
  const org = requirements?.org;
  const contacts = requirements?.contacts || [];

  useEffect(() => {
    if (!open || !requirements) return;
    setError('');
    setOrgForm({
      raisonSociale: org?.raisonSociale || '',
      nomCommercial: org?.nomCommercial || '',
      siren: org?.siren || '',
      siret: org?.siret || '',
      tvaIntracommunautaire: org?.tvaIntracommunautaire || '',
      adresse: org?.adresse || '',
      codePostal: org?.codePostal || '',
      ville: org?.ville || '',
      pays: org?.pays || 'FR',
      emailPrincipal: org?.emailPrincipal || '',
      emailFacturation: org?.emailFacturation || '',
    });
    setSignatoryContactId(requirements.signatoryContactId || '');
    const contactList = requirements.contacts || [];
    setSignatoryMode(
      requirements.signatoryContactId || contactList.length
        ? 'select'
        : 'create',
    );
    setNewContact({ prenom: '', nom: '', email: '', fonction: '' });
    setPrimaryContact({ prenom: '', nom: '', email: '' });
    setValidUntil(requirements.quoteMeta?.validUntil?.slice?.(0, 10) || requirements.quoteMeta?.validUntil || '');
    setQuoteContactId(requirements.quoteMeta?.contactId || '');
    setTemplateVersionId('');
  }, [open, requirements, org]);

  const selectedSignatory = useMemo(
    () => contacts.find((c) => c.id === signatoryContactId) || null,
    [contacts, signatoryContactId],
  );

  const signatoryIncomplete = useMemo(
    () => (blocks.includes('signatory')
      ? computeContractSignatoryGateMissing(
        signatoryMode === 'create'
          ? { ...newContact }
          : selectedSignatory,
      )
      : []),
    [blocks, signatoryMode, newContact, selectedSignatory],
  );

  if (!open || !requirements) return null;

  async function patchOrgFields() {
    if (!org?.id) return org;
    const mappedOrgFields = mapConversionMissingToOrgFields(missingKeys);
    const mayPatchOrg = blocks.includes('orgLegal') || mappedOrgFields.length > 0;
    if (!mayPatchOrg) return org;
    const patch = {};
    const fields = [
      'raisonSociale', 'nomCommercial', 'siren', 'siret', 'tvaIntracommunautaire',
      'adresse', 'codePostal', 'ville', 'pays', 'emailPrincipal', 'emailFacturation',
    ];
    for (const f of fields) {
      const needed = orgFieldNeeded(missingKeys, f)
        || missingKeys.includes('email')
        || (f.startsWith('email') && missingKeys.includes('recipientEmail'));
      if (!needed && orgForm[f] === (org[f] || '')) continue;
      if (orgForm[f] !== undefined) {
        let val = typeof orgForm[f] === 'string' ? orgForm[f].trim() : orgForm[f];
        if (f === 'siren' && val) val = normalizeSiren(val) || val;
        patch[f] = val;
      }
    }
    if (!Object.keys(patch).length) return org;
    const res = await apiService.updateNr2jOrganization(org.id, patch);
    return res?.organization || res;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (blocks.includes('contractOffer') && missingKeys.includes('lines')) {
      setError('Ajoutez au moins une ligne d’offre sur la fiche contrat, puis relancez l’envoi.');
      return;
    }
    if (showOrgField('siren') && orgFieldNeeded(missingKeys, 'siren')) {
      const sirenCheck = validateSirenInput(orgForm.siren);
      if (!sirenCheck.ok) {
        setError(sirenCheck.message);
        return;
      }
    }

    setBusy(true);
    try {
      let nextOrg = org;
      let nextContacts = [...contacts];
      let resolvedSignatoryId = signatoryContactId;

      nextOrg = await patchOrgFields();

      if (blocks.includes('primaryContact') && org?.id) {
        const pc = primaryContact;
        if (!pc.prenom?.trim() && !pc.nom?.trim()) {
          setError('Contact principal : indiquez au moins le prénom ou le nom.');
          return;
        }
        if (!pc.email?.trim() || !/@/.test(pc.email)) {
          setError('Contact principal : email valide requis.');
          return;
        }
        const res = await apiService.createNr2jContact(org.id, {
          prenom: pc.prenom.trim(),
          nom: pc.nom.trim(),
          email: pc.email.trim(),
          isPrimary: true,
        });
        const created = res?.contact || res;
        nextContacts = [...nextContacts, created];
      }

      if (blocks.includes('signatory') && org?.id) {
        if (signatoryMode === 'create') {
          if (signatoryIncomplete.length) {
            setError('Complétez le signataire (nom, email, fonction).');
            return;
          }
          const res = await apiService.createNr2jContact(org.id, {
            prenom: newContact.prenom.trim(),
            nom: newContact.nom.trim(),
            email: newContact.email.trim(),
            fonction: newContact.fonction.trim(),
          });
          const created = res?.contact || res;
          nextContacts = [...nextContacts, created];
          resolvedSignatoryId = created.id;
        } else if (selectedSignatory) {
          if (signatoryIncomplete.length) {
            const res = await apiService.updateNr2jContact(selectedSignatory.id, {
              prenom: (newContact.prenom || selectedSignatory.prenom || '').trim(),
              nom: (newContact.nom || selectedSignatory.nom || '').trim(),
              email: (newContact.email || selectedSignatory.email || '').trim(),
              fonction: (newContact.fonction || selectedSignatory.fonction || '').trim(),
            });
            const updated = res?.contact || res;
            nextContacts = nextContacts.map((c) => (c.id === updated.id ? updated : c));
          }
          resolvedSignatoryId = selectedSignatory.id;
        } else {
          setError('Choisissez ou créez un signataire.');
          return;
        }
      }

      if (blocks.includes('quoteMeta') && requirements.quoteId && requirements.versionId) {
        const payload = {};
        if (missingKeys.includes('validUntil') || validUntil) {
          payload.validUntil = validUntil ? new Date(validUntil).toISOString() : null;
        }
        if (missingKeys.includes('recipientEmail') || quoteContactId) {
          payload.contactId = quoteContactId || null;
        }
        if (Object.keys(payload).length) {
          await apiService.patchNr2jQuoteVersion(
            requirements.quoteId,
            requirements.versionId,
            payload,
          );
        }
      }

      if (blocks.includes('contractOffer') && requirements.contractId) {
        const patch = {};
        if (missingKeys.includes('templateVersionId') && templateVersionId) {
          patch.templateVersionId = templateVersionId;
        }
        if (Object.keys(patch).length) {
          await apiService.patchNr2jContract(requirements.contractId, patch);
        }
      }

      await onSaved?.({
        org: nextOrg,
        contacts: nextContacts,
        signatoryContactId: resolvedSignatoryId,
        validUntil: validUntil ? new Date(validUntil).toISOString() : null,
        quoteContactId,
      });
    } catch (err) {
      setError(err?.message || 'Enregistrement impossible');
    } finally {
      setBusy(false);
    }
  }

  function showOrgField(field) {
    return orgFieldNeeded(missingKeys, field)
      || ['raisonSociale', 'nomCommercial', 'siren', 'siret', 'adresse', 'codePostal', 'ville', 'pays',
        'emailPrincipal', 'emailFacturation'].some((f) => missingKeys.includes(f) && orgFieldNeeded(missingKeys, f));
  }

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
      data-testid="nr2j-continue-modal"
    >
      <form
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl bg-white p-5 shadow-xl"
        onSubmit={handleSubmit}
      >
        <h3 className="text-lg font-semibold text-gray-900">{requirements.title}</h3>
        <p className="mt-1 text-sm text-gray-600">
          Complétez les champs ci-dessous. L’action reprendra automatiquement après validation.
        </p>

        {missingKeys.length ? (
          <ul className="mt-3 space-y-0.5 text-xs text-amber-900">
            {missingKeys.map((key) => (
              <li key={key}>
                ⚠ {NR2J_CONVERSION_MISSING_LABELS[key]
                  || NR2J_QUOTE_SEND_GATE_LABELS[key]
                  || contractSendGateLabel(key)}
              </li>
            ))}
          </ul>
        ) : null}

        {blocks.includes('orgLegal') ? (
          <section className="mt-4 space-y-3 border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-800">Société</h4>
            {(showOrgField('raisonSociale') || showOrgField('nomCommercial')) ? (
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="text-gray-500">Raison sociale</span>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={orgForm.raisonSociale}
                    onChange={(ev) => setOrgForm((p) => ({ ...p, raisonSociale: ev.target.value }))}
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-500">Nom commercial</span>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={orgForm.nomCommercial}
                    onChange={(ev) => setOrgForm((p) => ({ ...p, nomCommercial: ev.target.value }))}
                  />
                </label>
              </div>
            ) : null}
            {showOrgField('siren') ? (
              <label className="block text-sm">
                <span className="text-gray-500">SIREN</span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={orgForm.siren}
                  onChange={(ev) => setOrgForm((p) => ({ ...p, siren: ev.target.value }))}
                />
              </label>
            ) : null}
            {showOrgField('siret') ? (
              <label className="block text-sm">
                <span className="text-gray-500">SIRET</span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={orgForm.siret}
                  onChange={(ev) => setOrgForm((p) => ({ ...p, siret: ev.target.value }))}
                />
              </label>
            ) : null}
            {showOrgField('adresse') ? (
              <label className="block text-sm">
                <span className="text-gray-500">Adresse</span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={orgForm.adresse}
                  onChange={(ev) => setOrgForm((p) => ({ ...p, adresse: ev.target.value }))}
                />
              </label>
            ) : null}
            <div className="grid gap-2 sm:grid-cols-3">
              {showOrgField('codePostal') ? (
                <label className="block text-sm">
                  <span className="text-gray-500">CP</span>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={orgForm.codePostal}
                    onChange={(ev) => setOrgForm((p) => ({ ...p, codePostal: ev.target.value }))}
                  />
                </label>
              ) : null}
              {showOrgField('ville') ? (
                <label className="block text-sm sm:col-span-2">
                  <span className="text-gray-500">Ville</span>
                  <input
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={orgForm.ville}
                    onChange={(ev) => setOrgForm((p) => ({ ...p, ville: ev.target.value }))}
                  />
                </label>
              ) : null}
            </div>
            {showOrgField('pays') ? (
              <label className="block text-sm">
                <span className="text-gray-500">Pays</span>
                <input
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={orgForm.pays}
                  onChange={(ev) => setOrgForm((p) => ({ ...p, pays: ev.target.value }))}
                />
              </label>
            ) : null}
            {(showOrgField('emailPrincipal') || missingKeys.includes('email') || missingKeys.includes('recipientEmail')) ? (
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="text-gray-500">Email principal</span>
                  <input
                    type="email"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={orgForm.emailPrincipal}
                    onChange={(ev) => setOrgForm((p) => ({ ...p, emailPrincipal: ev.target.value }))}
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-gray-500">Email facturation</span>
                  <input
                    type="email"
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={orgForm.emailFacturation}
                    onChange={(ev) => setOrgForm((p) => ({ ...p, emailFacturation: ev.target.value }))}
                  />
                </label>
              </div>
            ) : null}
          </section>
        ) : null}

        {blocks.includes('primaryContact') ? (
          <section className="mt-4 space-y-3 border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-800">Contact principal</h4>
            <div className="grid gap-2 sm:grid-cols-2">
              <input
                placeholder="Prénom"
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={primaryContact.prenom}
                onChange={(ev) => setPrimaryContact((p) => ({ ...p, prenom: ev.target.value }))}
              />
              <input
                placeholder="Nom"
                className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={primaryContact.nom}
                onChange={(ev) => setPrimaryContact((p) => ({ ...p, nom: ev.target.value }))}
              />
            </div>
            <input
              type="email"
              placeholder="Email"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={primaryContact.email}
              onChange={(ev) => setPrimaryContact((p) => ({ ...p, email: ev.target.value }))}
            />
          </section>
        ) : null}

        {blocks.includes('signatory') ? (
          <section className="mt-4 space-y-3 border-t pt-4" data-testid="nr2j-continue-signatory-block">
            <h4 className="text-sm font-semibold text-gray-800">Signataire du contrat</h4>
            <div className="flex gap-2 text-sm">
              <button
                type="button"
                className={`rounded-lg border px-3 py-1.5 ${signatoryMode === 'select' ? 'border-indigo-500 bg-indigo-50' : ''}`}
                onClick={() => setSignatoryMode('select')}
              >
                Contact existant
              </button>
              <button
                type="button"
                className={`rounded-lg border px-3 py-1.5 ${signatoryMode === 'create' ? 'border-indigo-500 bg-indigo-50' : ''}`}
                onClick={() => setSignatoryMode('create')}
              >
                + Créer un contact
              </button>
            </div>
            {signatoryMode === 'select' ? (
              <select
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                value={signatoryContactId}
                onChange={(e) => setSignatoryContactId(e.target.value)}
              >
                <option value="">— Choisir —</option>
                {contacts.map((c) => (
                  <option key={c.id} value={c.id}>
                    {contactDisplayName(c) || c.id}
                    {c.email ? ` (${c.email})` : ''}
                  </option>
                ))}
              </select>
            ) : null}
            {(signatoryMode === 'create' || (signatoryMode === 'select' && selectedSignatory && signatoryIncomplete.length)) ? (
              <div className="grid gap-2 sm:grid-cols-2">
                <input
                  placeholder="Prénom"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  value={newContact.prenom || selectedSignatory?.prenom || ''}
                  onChange={(ev) => setNewContact((p) => ({ ...p, prenom: ev.target.value }))}
                />
                <input
                  placeholder="Nom"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  value={newContact.nom || selectedSignatory?.nom || ''}
                  onChange={(ev) => setNewContact((p) => ({ ...p, nom: ev.target.value }))}
                />
                <input
                  type="email"
                  placeholder="Email"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm sm:col-span-2"
                  value={newContact.email || selectedSignatory?.email || ''}
                  onChange={(ev) => setNewContact((p) => ({ ...p, email: ev.target.value }))}
                />
                <input
                  placeholder="Fonction"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm sm:col-span-2"
                  value={newContact.fonction || selectedSignatory?.fonction || ''}
                  onChange={(ev) => setNewContact((p) => ({ ...p, fonction: ev.target.value }))}
                />
              </div>
            ) : null}
          </section>
        ) : null}

        {blocks.includes('quoteMeta') ? (
          <section className="mt-4 space-y-3 border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-800">Devis</h4>
            {missingKeys.includes('validUntil') ? (
              <label className="block text-sm">
                <span className="text-gray-500">Validité jusqu’au</span>
                <input
                  type="date"
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={validUntil}
                  onChange={(ev) => setValidUntil(ev.target.value)}
                />
              </label>
            ) : null}
            {missingKeys.includes('recipientEmail') ? (
              <label className="block text-sm">
                <span className="text-gray-500">Contact destinataire</span>
                <select
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                  value={quoteContactId}
                  onChange={(e) => setQuoteContactId(e.target.value)}
                >
                  <option value="">Email société par défaut</option>
                  {contacts.map((c) => (
                    <option key={c.id} value={c.id}>{contactDisplayName(c)} ({c.email || '—'})</option>
                  ))}
                </select>
              </label>
            ) : null}
          </section>
        ) : null}

        {blocks.includes('contractOffer') && missingKeys.includes('templateVersionId') ? (
          <section className="mt-4 space-y-2 border-t pt-4">
            <h4 className="text-sm font-semibold text-gray-800">Modèle de contrat</h4>
            <select
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              value={templateVersionId}
              onChange={(e) => setTemplateVersionId(e.target.value)}
            >
              <option value="">— Choisir un modèle publié —</option>
              {templateOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.label}</option>
              ))}
            </select>
          </section>
        ) : null}

        {error ? (
          <p className="mt-3 text-sm text-rose-700" data-testid="nr2j-continue-error">{error}</p>
        ) : null}

        <div className="mt-5 flex gap-2">
          <button
            type="submit"
            disabled={busy}
            className="flex-1 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white disabled:opacity-50"
            data-testid="nr2j-continue-submit"
          >
            {busy ? 'Enregistrement…' : 'Continuer'}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            className="rounded-lg border px-3 py-2 text-sm"
            data-testid="nr2j-continue-cancel"
          >
            Annuler
          </button>
        </div>
      </form>
    </div>
  );
}

export { NR2J_CONTINUE_INTENTS };
